/* eslint-disable no-console */
import React, { lazy, Suspense, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { Button, DatePicker, Icon } from "antd"

import { pageChanged } from "../../../../../data/redux/page_details/actions"
import { getIsMobile } from "../../../../../data/redux/page_details/selectors"
import {
  updateFractureState,
  getAllFractureData,
  updateFractureData
} from "../../../../../data/redux/fracture_details/actions"

import { APP_PAGES } from "../../../../../data/config/constants"
import { log } from "../../../../../data/config/utils"
import { IMG_EMPTY } from "../../../../../data/assets/img"

import SpinnerLoader from "../../../../../components/spinnerloader"

const Graph = lazy(() => import("../../../components/charts/graph"))
const ErrorBoundary = lazy(() => import("../../../../../components/errorboundary"))
const CustomSelect = lazy(() => import("../../../../../components/form/CustomSelect"))
const ParentChildSelect = lazy(() => import("../../../../../components/form/ParentChildSelect"))
const SelectItem = lazy(() => import("../../../../../components/form/selectitem"))
const ShowMessage = lazy(() => import("../../../../../components/showmessage"))

const { RangePicker } = DatePicker

const selectWidth = "100px"

const Fracture = ({
  actions,
  is_mobile,
  fracture_details
}) => {
  const hasMounted = useRef(false)
  const { pageChanged, updateFractureState, getAllFractureData, updateFractureData } = actions
  const { fracture_list, filters, selected_filters, selected_date } = fracture_details

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      pageChanged(APP_PAGES.FRACTURE, APP_PAGES.ANALYTICS)
      getAllFractureData(selected_date)
    }
  }, [pageChanged, getAllFractureData, selected_date])

  const loadMoreFractureData = (fracture, type) => {
    const payload = {
      id: fracture.id,
      ...selected_filters,
      ...selected_date,
      localFilter: fracture.selectedFilters
    }
    if (type === "next") {
      payload.currentPosition = fracture.position
      payload.sendNext = true
    } else if (type === "prev") {
      payload.currentPosition = fracture.position
      payload.sendPrev = true
    } else {
      payload.currentPosition = fracture.position
      payload.sendMore = true
    }
    log("loadMoreFractureData payload", payload)
    updateFractureData(payload)
  }

  const handleLocalFilterChange = (changedValue = {}, fracture) => {
    log("handleLocalFilterChange", changedValue, fracture)
    const payload = {
      ...selected_filters,
      ...selected_date,
      id: fracture.id,
      localFilter: {
        ...fracture.selectedFilters,
        ...changedValue
      }
    }
    log("handleLocalFilterChange payload", payload)
    updateFractureData(payload)
  }

  const handleGlobalFilterChange = (changedValue = {}) => {
    log("handleGlobalFilterChange", changedValue)
    const payload = {
      ...selected_filters,
      ...selected_date,
      ...changedValue
    }
    getAllFractureData(payload)
  }

  const handleDateRangeChange = dateRange => {
    log("handleDateRangeChange", dateRange)
    const payload = {
      ...selected_filters,
      startDate: dateRange[0],
      endDate: dateRange[1]
    }
    getAllFractureData(payload, () => {
      updateFractureState({
        selected_date: {
          startDate: dateRange[0],
          endDate: dateRange[1]
        }
      })
    })
  }

  const renderGlobalFilters = () => {
    if (filters && filters.length > 0)
      return (
        <Suspense fallback={null}>
          {
            filters.map((filter, index) => {
              switch (filter.type) {
                case "select":
                  return (
                    <ErrorBoundary key={index}>
                      <SelectItem
                        container_class="ori-lr-pad-5 ori-width-100"
                        key={index}
                        select_class=""
                        size="small"
                        placeholder={filter.placeholder}
                        value_key={filter.valueKey}
                        name_key={filter.labelKey}
                        is_option_object_type={filter.isOptionObjectType}
                        value={selected_filters[filter.name]}
                        options={filter.options}
                        onChange={value => handleGlobalFilterChange({ [filter.name]: value })}
                      />
                    </ErrorBoundary>
                  )

                case "customSelect":
                  return (
                    <ErrorBoundary key={index}>
                      <CustomSelect
                        container_class="ori-lr-pad-5 ori-b-mrgn-10"
                        default_custom={filter.custom}
                        select_props={{
                          size: "small",
                          placeholder: "Select",
                          style: { width: selectWidth },
                          value: typeof selected_filters[filter.name] === "string" ? selected_filters[filter.name] : null,
                          onChange: value => handleGlobalFilterChange({ [filter.name]: value })
                        }}
                        options={filter.options}
                        custom_select_props={{
                          size: "small",
                          placeholder: "Choose multiple",
                          mode: "multiple",
                          style: { minWidth: selectWidth, maxWidth: "220px" }
                        }}
                        custom_value={selected_filters[filter.name]}
                        custom_options={filter.customOptions}
                        onApply={value => handleGlobalFilterChange({ [filter.name]: value })}
                      />
                    </ErrorBoundary>
                  )

                case "parentChildSelect":
                  return (
                    <ErrorBoundary key={index}>
                      <ParentChildSelect
                        parent_select_props={{
                          size: "small",
                          placeholder: filter.parentPlaceholder,
                          style: { width: selectWidth },
                          className: "ori-lr-pad-5 ori-width-100"
                        }}
                        child_select_props={{
                          size: "small",
                          placeholder: filter.childPlaceholder,
                          style: { width: selectWidth },
                          className: "ori-lr-pad-5 ori-width-100"
                        }}
                        value={[selected_filters[filter.parentName], selected_filters[filter.childName]]}
                        data={filter.options}
                        onChange={value => handleGlobalFilterChange({
                          [filter.parentName]: value[0],
                          [filter.childName]: value[1]
                        })}
                      />
                    </ErrorBoundary>
                  )

                default: return null
              }
            })
          }
        </Suspense>
      )
  }

  const renderLocalFilters = fracture => {
    if (fracture.filters && fracture.filters.length > 0)
      return (
        <Suspense fallback={null}>
          {
            fracture.filters.map((filter, index) => {
              switch (filter.type) {
                case "select":
                  return (
                    <ErrorBoundary key={index}>
                      <SelectItem
                        container_class="ori-lr-pad-5 ori-width-100"
                        select_class=""
                        size="small"
                        placeholder={filter.placeholder}
                        value_key={filter.valueKey}
                        name_key={filter.labelKey}
                        is_option_object_type={filter.isOptionObjectType}
                        value={fracture.selectedFilters[filter.name]}
                        options={filter.options}
                        onChange={value => handleLocalFilterChange({ [filter.name]: value }, fracture)}
                      />
                    </ErrorBoundary>
                  )

                case "customSelect":
                  return (
                    <ErrorBoundary key={index}>
                      <CustomSelect
                        container_class="ori-lr-pad-5 ori-b-mrgn-10"
                        default_custom={filter.custom}
                        select_props={{
                          size: "small",
                          placeholder: "Select",
                          style: { width: selectWidth },
                          value: typeof fracture.selectedFilters[filter.name] === "string" ? fracture.selectedFilters[filter.name] : null,
                          onChange: value => handleLocalFilterChange({ [filter.name]: value }, fracture)
                        }}
                        options={filter.options}
                        custom_select_props={{
                          size: "small",
                          placeholder: "Choose multiple",
                          mode: "multiple",
                          style: { minWidth: selectWidth, maxWidth: "300px" }
                        }}
                        custom_value={fracture.selectedFilters[filter.name]}
                        custom_options={filter.customOptions}
                        onApply={value => handleLocalFilterChange({ [filter.name]: value }, fracture)}
                      />
                    </ErrorBoundary>
                  )

                case "parentChildSelect":
                  return (
                    <ErrorBoundary key={index}>
                      <ParentChildSelect
                        parent_select_props={{
                          size: "small",
                          placeholder: filter.parentPlaceholder,
                          style: { width: selectWidth },
                          className: "ori-lr-mrgn-5"
                        }}
                        child_select_props={{
                          size: "small",
                          placeholder: filter.childPlaceholder,
                          style: { width: selectWidth },
                          className: "ori-lr-mrgn-5"
                        }}
                        value={[fracture.selectedFilters[filter.parentName], fracture.selectedFilters[filter.childName]]}
                        data={filter.options}
                        onChange={value => handleLocalFilterChange(
                          {
                            [filter.parentName]: value[0],
                            [filter.childName]: value[1]
                          },
                          fracture
                        )}
                      />
                    </ErrorBoundary>
                  )

                default: return null
              }
            })
          }
        </Suspense>
      )
  }

  const renderFractureItem = fracture => {
    if (fracture.data && fracture.data.length === 0)
      return (
        <ShowMessage
          img_src={IMG_EMPTY}
          height={200}
          width={250}
          title="Data not found"
          message="fracture data not found for this filter please select another filter."
        />
      )

    switch (fracture.type) {
      case "histogram":
        return (
          <Graph
            type="interval"
            data={fracture.data}
            scale={fracture.scale}
            x_axis={fracture.axis.x}
            y_axis={fracture.axis.y}
            x_axis_props={{ label: { offset: 8 } }}
            height={250 + (fracture.data.length / 10) * 200}
            padding={is_mobile ? [20, 20, 20, 80] : ["auto", 20, "auto", "auto"]}
            color_key={fracture.axis.color}
            coord_props={{ transpose: true, reflect: "y" }}
            forceFit
          />
        )

      case "line":
        return (
          <Graph
            type="line"
            data={fracture.data}
            scale={fracture.scale}
            x_axis={fracture.axis.x}
            y_axis={fracture.axis.y}
            height={350}
            color_key={fracture.axis.color}
            padding={[60, 25, 50, 60]}
            legend
          />
        )

      default:
        return null
    }
  }

  return (
    <div className="ori-lr-pad-10">
      <div className="ori-t-pad-15 ori-flex ori-flex-jsb ori-flex-wrap ori-b-border-default ori-b-mrgn-10">
        <p className="ori-b-mrgn-15">{APP_PAGES.FRACTURE}</p>
        <div className="ori-flex-row ori-flex-wrap">
          {renderGlobalFilters()}
          <RangePicker
            className="ori-l-mrgn-10"
            size="small"
            placeholder={["start time", "end time"]}
            format="DD-MM-YYYY"
            allowClear={false}
            value={[selected_date.startDate, selected_date.endDate]}
            onChange={handleDateRangeChange}
          />
        </div>
      </div>
      <Suspense fallback={<SpinnerLoader text="Loading" />}>
        {
          fracture_list.map((fracture, index) => {
            return (
              <ErrorBoundary key={index}>
                <div className="ori-animated ori-fade-in ori-box-shadow-light ori-tb-mrgn-10 ori-bg-white ori-lr-pad-15 ori-b-pad-10 ori-border-radius-4">
                  {
                    (fracture.title || fracture.filters) &&
                    <div
                      className="ori-t-pad-15 ori-b-mrgn-15 ori-flex-row ori-flex-jsb ori-flex-wrap"
                      style={{ minHeight: "50px" }}
                    >
                      <p className="ori-b-mrgn-10">{fracture.title}</p>
                      <div className="ori-flex-row ori-flex-wrap">
                        {renderLocalFilters(fracture)}
                      </div>
                    </div>
                  }
                  <Suspense fallback={<SpinnerLoader />}>
                    {renderFractureItem(fracture)}
                  </Suspense>
                  {
                    fracture.hasMore &&
                    <Button
                      size="small"
                      className="ori-b-mrgn-5 ori-display-block ori-l-mrgn-auto"
                      onClick={() => loadMoreFractureData(fracture)}
                    >
                      Show more
                    </Button>
                  }
                  {
                    fracture.hasNext !== undefined && fracture.hasPrev !== undefined &&
                    <div className="ori-flex-row ori-flex-jfe ori-flex-wrap ori-b-pad-5">
                      <Button.Group size="small">
                        <Button
                          disabled={!fracture.hasPrev}
                          onClick={() => loadMoreFractureData(fracture, "prev")}
                        >
                          <Icon type="left" /> Prev
                          </Button>
                        <Button
                          disabled={!fracture.hasNext}
                          onClick={() => loadMoreFractureData(fracture, "next")}
                        >
                          Next <Icon type="right" />
                        </Button>
                      </Button.Group>
                    </div>
                  }
                </div>
              </ErrorBoundary>
            )
          })
        }
        {
          fracture_list.length === 0 &&
          <ShowMessage
            img_src={IMG_EMPTY}
            height={200}
            width={250}
            title="Data not found"
            message="fracture data not found for this filter please select another filter"
          />
        }
      </Suspense>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    fracture_details: state.fracture_details,
    is_mobile: getIsMobile(state),
  }
}

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        pageChanged,
        updateFractureState,
        getAllFractureData,
        updateFractureData
      },
      dispatch
    )
  }
}

Fracture.propTypes = {
  actions: PropTypes.object,
  fracture_details: PropTypes.object,
  is_mobile: PropTypes.bool
}

export default connect(mapStateToProps, mapDispatchToProps)(Fracture)



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/analytics/Fracture/index.js